import { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import type { ArticleData } from '@/mocks/articles';

interface SeoArticlePageProps {
  article: ArticleData;
}

export default function SeoArticlePage({ article }: SeoArticlePageProps) {
  const { pathname } = useLocation();
  const siteUrl = import.meta.env.VITE_SITE_URL || '';
  const pageUrl = siteUrl ? `${siteUrl}${pathname}` : pathname;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <div className="min-h-screen bg-background-50">
      <header>
        <Navbar />
      </header>

      <main>
        <article>
          <section className="w-full bg-background-100 pt-28 md:pt-32 pb-10 md:pb-14 px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6 flex-wrap">
                <Link to="/" className="hover:text-accent-600 transition-colors duration-150 whitespace-nowrap">Home</Link>
                <i className="ri-arrow-right-s-line text-[10px]"></i>
                <span className="text-foreground-700 whitespace-nowrap">{article.tag}</span>
              </nav>

              <span className="inline-block px-3 py-1 text-xs font-medium bg-accent-100 text-accent-700 rounded-full mb-4 whitespace-nowrap">
                {article.tag}
              </span>

              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-5 leading-tight">
                {article.title}
              </h1>

              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6 max-w-2xl">
                {article.description}
              </p>

              <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-500">
                <span className="flex items-center gap-1.5 whitespace-nowrap">
                  <i className="ri-calendar-line"></i>
                  Updated August 2026
                </span>
                <span className="flex items-center gap-1.5 whitespace-nowrap">
                  <i className="ri-time-line"></i>
                  {article.readTime}
                </span>
                <span className="flex items-center gap-1.5 whitespace-nowrap">
                  <i className="ri-checkbox-circle-line"></i>
                  Verified &amp; Researched
                </span>
              </div>
            </div>
          </section>

          <section className="w-full bg-background-50 py-10 md:py-14 px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="prose-custom space-y-8">
                {article.sections.map((section, sIdx) => (
                  <div key={sIdx}>
                    {section.title && (
                      <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                        {section.title}
                      </h2>
                    )}

                    {section.paragraphs && section.paragraphs.map((p, pIdx) => (
                      <p key={pIdx} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4" dangerouslySetInnerHTML={{ __html: p }}></p>
                    ))}

                    {section.table && (
                      <div className="overflow-x-auto mb-5">
                        <table className="w-full text-sm border-collapse">
                          <thead>
                            <tr className="border-b border-background-200/70">
                              {section.table.headers.map((h, hIdx) => (
                                <th key={hIdx} className="text-left py-3 px-4 font-semibold text-foreground-800 whitespace-nowrap">{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {section.table.rows.map((row, rIdx) => (
                              <tr key={rIdx} className="border-b border-background-200/50 hover:bg-background-100 transition-colors">
                                {row.map((cell, cIdx) => (
                                  <td key={cIdx} className="py-3 px-4 text-foreground-700 whitespace-nowrap">{cell}</td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {section.tips && (
                      <div className="space-y-3 mt-3">
                        {section.tips.map((tip, tIdx) => (
                          <div key={tIdx} className="flex items-start gap-3 p-4 bg-background-100 rounded-xl border border-background-200/70">
                            <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center mt-0.5">
                              <span className="text-xs font-semibold text-primary-600">{tIdx + 1}</span>
                            </span>
                            <div>
                              <h4 className="text-sm font-semibold text-foreground-800 mb-1">{tip.title}</h4>
                              <p className="text-sm text-foreground-600 leading-relaxed">{tip.desc}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {section.list && (
                      <ul className="space-y-2 mb-4">
                        {section.list.map((item, lIdx) => (
                          <li key={lIdx} className="flex items-start gap-2 text-sm text-foreground-700">
                            <i className="ri-check-line text-accent-500 mt-0.5 flex-shrink-0"></i>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>

              <div className="mt-10 pt-8 border-t border-background-200/70">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 text-sm text-foreground-600 hover:text-accent-600 transition-colors duration-150 whitespace-nowrap"
                  >
                    <i className="ri-arrow-left-line"></i>
                    Back to Homepage
                  </Link>
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white rounded-full text-sm font-semibold hover:bg-primary-600 transition-all duration-200 whitespace-nowrap"
                  >
                    Explore Sugargoo Spreadsheet
                    <i className="ri-external-link-line"></i>
                  </a>
                </div>
              </div>
            </div>
          </section>
        </article>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.description,
            url: pageUrl,
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': pageUrl,
            },
            datePublished: '2026-08-01',
            dateModified: '2026-08-05',
            author: {
              '@type': 'Organization',
              name: 'Sugargoo Spreadsheet',
              url: siteUrl || undefined,
            },
            publisher: {
              '@type': 'Organization',
              name: 'Sugargoo Spreadsheet',
              url: siteUrl || undefined,
            },
          }),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: siteUrl ? `${siteUrl}/` : '/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: article.tag,
                item: pageUrl,
              },
            ],
          }),
        }}
      />
    </div>
  );
}